import Guid from 'core/types/Guid';
import { ApolloProvider } from 'core/net/ApolloProvider';

import { FETCH_WEATHER_STATIONS } from './graphql/facility.fetchWeatherStations';
import { IWeatherStation, IWeatherStationSensor } from 'app/business-logic/domain-models/Weather';
import { FetchWeatherStationsQuery, FetchWeatherStationsQueryVariables } from 'app/__generated__/global';

type SurfaceWeatherStation = NonNullable<
  NonNullable<FetchWeatherStationsQuery['facility']>['monitorConfiguration']
>['surfaceWeatherStations'][number];

type SensorKey = Extract<
  keyof SurfaceWeatherStation,
  'precipitationSensor' | 'humiditySensor' | 'temperatureSensor' | 'windDirectionSensor' | 'windSpeedSensor'
>;

type WeatherStationSensor = SurfaceWeatherStation[SensorKey];

export async function fetchWeatherStations(
  weatherStationIds: Guid[] = [],
  isDefault: boolean | null = null
): Promise<IWeatherStation[]> {
  const variables: FetchWeatherStationsQueryVariables = {
    filter: {
      id_in: weatherStationIds,
      is_default: isDefault,
    },
  };

  const { data } = await ApolloProvider.global().query<FetchWeatherStationsQuery, FetchWeatherStationsQueryVariables>({
    query: FETCH_WEATHER_STATIONS,
    variables,
  });

  const sensorCounts = new Map<Guid, number>();

  return (
    data?.facility?.monitorConfiguration?.surfaceWeatherStations
      ?.flatMap(surfaceWeatherStationConfiguration => {
        const {
          isDefault,
          monitor,
          precipitationSensor,
          humiditySensor,
          temperatureSensor,
          windDirectionSensor,
          windSpeedSensor,
        } = surfaceWeatherStationConfiguration;
        if (!monitor?.id || !monitor?.name) return [];
        return {
          monitorId: monitor.id,
          monitorName: monitor.name,
          isDefault: !!isDefault,
          precipitationSensor: convertToWeatherStationSensor(precipitationSensor),
          humiditySensor: convertToWeatherStationSensor(humiditySensor),
          temperatureSensor: convertToWeatherStationSensor(temperatureSensor),
          windDirectionSensor: convertToWeatherStationSensor(windDirectionSensor),
          windSpeedSensor: convertToWeatherStationSensor(windSpeedSensor),
        } satisfies IWeatherStation;
      })
      .sort((left: IWeatherStation, right: IWeatherStation) => {
        if (left.isDefault) return -1;
        if (right.isDefault) return 1;
        return getSensorCount(right) - getSensorCount(left);
      }) ?? []
  );

  ////////////////////

  function getSensorCount(weatherStation: IWeatherStation) {
    let sensorCount = sensorCounts.get(weatherStation.monitorId);

    if (sensorCount === undefined) {
      const { humiditySensor, temperatureSensor, windDirectionSensor, windSpeedSensor } = weatherStation;
      sensorCount = [humiditySensor, temperatureSensor, windDirectionSensor, windSpeedSensor].filter(
        sensor => !!sensor
      ).length;

      sensorCounts.set(weatherStation.monitorId, sensorCount);
    }

    return sensorCount;
  }
}

function convertToWeatherStationSensor(sensor: WeatherStationSensor): IWeatherStationSensor | null {
  if (!sensor) {
    return null;
  }

  const { id, name, displayUnit } = sensor;

  return {
    id,
    name,
    unit: displayUnit?.name ?? '',
  };
}
